/**
 * Complementa os filtros nativos da Shopify ocultando produtos cuja variante
 * filtrada não está disponível. Não altera URLs, paginação nem facetas nativas.
 */
(function () {
  const FILTER_PREFIX = 'filter.v.option.';
  const PROCESSED_ATTR = 'data-variant-filter-applied';
  const GRID_SELECTORS = ['#product-grid', '#main-collection-product-grid', '#search-item-wrapper'];

  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function decodeFilterValue(value) {
    try {
      return decodeURIComponent(String(value || '').replace(/\+/g, ' '));
    } catch (error) {
      return String(value || '');
    }
  }

  function normalizeValue(value) {
    return decodeFilterValue(value).trim().toLowerCase();
  }

  function parseOptionFilters(search) {
    const params = new URLSearchParams(search || window.location.search);
    const filters = {};

    params.forEach((value, key) => {
      if (!key.startsWith(FILTER_PREFIX)) return;
      const optionHandle = key.slice(FILTER_PREFIX.length);
      if (!optionHandle) return;
      if (!filters[optionHandle]) filters[optionHandle] = [];
      filters[optionHandle].push(decodeFilterValue(value));
    });

    return filters;
  }

  function getGridRoots() {
    return GRID_SELECTORS.map((selector) => document.querySelector(selector)).filter(Boolean);
  }

  function getGridContainer() {
    return document.getElementById('ProductGridContainer') || document;
  }

  function getConfig() {
    const grid = document.querySelector('[data-variant-availability-filter]');
    return {
      enabled: grid ? grid.dataset.variantAvailabilityFilter !== 'false' : true,
      hideUnavailableButtons: grid ? grid.dataset.hideUnavailableVariantButtons === 'true' : false,
    };
  }

  function readVariantData(card) {
    const script = card.querySelector('.product-card-variants-data');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[variant-availability-filter] JSON inválido no card', error);
      return null;
    }
  }

  function variantMatchesFilters(variant, options, optionFilters) {
    return Object.entries(optionFilters).every(([optionHandle, filterValues]) => {
      const optionIndex = options.findIndex((option) => handleize(option) === optionHandle);
      if (optionIndex === -1) return true;

      const variantValue = variant[`option${optionIndex + 1}`];
      if (variantValue == null) return false;

      const normalizedVariantValue = normalizeValue(variantValue);
      return filterValues.some((filterValue) => normalizeValue(filterValue) === normalizedVariantValue);
    });
  }

  function hasAvailableMatchingVariant(data, optionFilters) {
    if (!data || !Array.isArray(data.variants)) return true;

    return data.variants.some((variant) => {
      if (!variant.available) return false;
      return variantMatchesFilters(variant, data.options || [], optionFilters);
    });
  }

  function setGridItemVisibility(gridItem, visible) {
    if (!gridItem) return;
    gridItem.hidden = !visible;
    gridItem.classList.toggle('variant-filter-hidden', !visible);
    gridItem.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function hideUnavailableVariantButtons(card) {
    card.querySelectorAll('.lista-tamanhos .tamanho.indisponivel').forEach((button) => {
      button.hidden = true;
      button.setAttribute('aria-hidden', 'true');
    });
  }

  function resetUnavailableVariantButtons(card) {
    card.querySelectorAll('.lista-tamanhos .tamanho.indisponivel').forEach((button) => {
      button.hidden = false;
      button.removeAttribute('aria-hidden');
    });
  }

  function processCard(card, optionFilters, config) {
    const gridItem = card.closest('.grid__item') || card.closest('[data-product-card]') || card;
    const hasOptionFilters = Object.keys(optionFilters).length > 0;

    if (!hasOptionFilters) {
      setGridItemVisibility(gridItem, true);
    } else {
      const data = readVariantData(card);
      const visible = hasAvailableMatchingVariant(data, optionFilters);
      setGridItemVisibility(gridItem, visible);
    }

    if (config.hideUnavailableButtons) {
      hideUnavailableVariantButtons(card);
    } else {
      resetUnavailableVariantButtons(card);
    }
  }

  function applyFilter(root) {
    const config = getConfig();
    if (!config.enabled) return;

    const scope = root || getGridContainer();
    const optionFilters = parseOptionFilters();
    const cards = scope.querySelectorAll('.product-card-wrapper');

    const currentSearch = window.location.search;

    cards.forEach((card) => {
      processCard(card, optionFilters, config);
      card.setAttribute(PROCESSED_ATTR, currentSearch);
    });

    document.dispatchEvent(
      new CustomEvent('collection:variant-availability-filtered', {
        detail: {
          optionFilters,
          visibleCount: scope.querySelectorAll('.grid__item:not(.variant-filter-hidden):not([hidden])').length,
        },
      })
    );
  }

  function debounce(fn, wait) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  const debouncedApply = debounce(() => applyFilter(), 50);

  function observeGridUpdates() {
    const container = getGridContainer();
    if (!container || container.__variantFilterObserver) return;

    const observer = new MutationObserver((mutations) => {
      const hasProductChanges = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          return (
            node.nodeType === 1 &&
            (node.matches?.('.product-card-wrapper, .grid__item') ||
              node.querySelector?.('.product-card-wrapper'))
          );
        });
      });

      if (hasProductChanges) debouncedApply();
    });

    observer.observe(container, { childList: true, subtree: true });
    container.__variantFilterObserver = observer;
  }

  function patchFacetRendering() {
    if (!window.FacetFiltersForm || window.FacetFiltersForm.__variantFilterPatched) return;

    const originalRender = FacetFiltersForm.renderProductGridContainer.bind(FacetFiltersForm);
    FacetFiltersForm.renderProductGridContainer = function patchedRenderProductGridContainer(html) {
      originalRender(html);
      applyFilter();
    };

    window.FacetFiltersForm.__variantFilterPatched = true;
  }

  window.CollectionVariantAvailabilityFilter = {
    apply: applyFilter,
    parseOptionFilters,
  };

  function init() {
    if (!getGridRoots().length && !document.getElementById('ProductGridContainer')) return;

    applyFilter();
    observeGridUpdates();
    patchFacetRendering();

    window.addEventListener('popstate', debouncedApply);
    document.addEventListener('shopify:section:load', debouncedApply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
